import type { TourCompletionRecord, TourSettings } from '@paintscout/ui'
import type { PageTriggerFields } from '@paintscout/util/engagement'
import { isPageViewTrigger, shouldShowOnPage } from '@paintscout/util/engagement'

/**
 * Pure helpers behind `AppTourProvider`'s engagement-table path (#25347,
 * docs/design/resource-center.md §4). They mirror the server's write rule in
 * `engagementEntity/tourState.ts`, so an optimistic update shows what the server will store.
 */

/** The `completion` of a `MyTour` from `myEngagement`. */
export interface EngagementTourCompletion {
  version: number
  completedAt: string
  dismissed: boolean
}

/**
 * What `read` answers while `tourManagement` is on and `myEngagement` has not loaded (offline with
 * no cached copy). Every tour counts as seen, so no tour opens by surprise.
 */
export const SEEN_EVERY_VERSION: TourCompletionRecord = {
  completedAt: '',
  version: Number.MAX_SAFE_INTEGER
}

/**
 * The record to read: the engagement record or the old `preferences.tours` one, whichever has the
 * higher version (the engagement one on a tie). The preferences fallback covers users the migration
 * task has not copied yet, and masquerade sessions, where `myEngagement` returns no tours.
 */
export function pickTourRecord(
  engagement: EngagementTourCompletion | null | undefined,
  preference: TourCompletionRecord | null | undefined
): TourCompletionRecord | null {
  const fromEngagement: TourCompletionRecord | null = engagement
    ? { completedAt: engagement.completedAt, version: engagement.version, dismissed: engagement.dismissed }
    : null
  const fromPreference = preference && typeof preference.version === 'number' ? preference : null
  if (!fromEngagement) return fromPreference
  if (!fromPreference) return fromEngagement
  return fromPreference.version > fromEngagement.version ? fromPreference : fromEngagement
}

/**
 * The completion after the user finishes or dismisses `version`, or null when the server would not
 * write: the stored record is already at a newer version, at the same version, or is a completion
 * the user now dismisses. A dismissal at the same version does become a completion.
 */
export function nextTourCompletion(
  current: EngagementTourCompletion | null | undefined,
  event: { version: number; dismissed: boolean; completedAt: string }
): EngagementTourCompletion | null {
  const next = { version: event.version, completedAt: event.completedAt, dismissed: event.dismissed }
  if (!current) return next
  if (current.version < event.version) return next
  if (current.version === event.version && current.dismissed && !event.dismissed) return next
  return null
}

/** Whether opening `version` is new for the user, so `startTour` would count it. */
export function isNewTourStart(startedVersion: number | null | undefined, version: number): boolean {
  return typeof startedVersion !== 'number' || startedVersion < version
}

type MyTourSettingsFields = { enabled: boolean; version: number } & PageTriggerFields

/**
 * Admin settings for one tour as the `Tour` auto-start check reads them (#25348). Null when the
 * flag is off or the tour has no entry: the tour then behaves as coded. While `myEngagement` has
 * not loaded, `read` already reports every tour as seen, so nothing auto-starts either way.
 *
 * A `page-view` tour (#25610) is enabled only while `pathname` matches its pattern, so it
 * auto-starts on that page and nowhere else. A replay button ignores settings, so it still works.
 */
export function toTourSettings(
  tourManagement: boolean,
  tour?: MyTourSettingsFields | null,
  pathname?: string | null
): TourSettings | null {
  if (!tourManagement || !tour) return null
  const onPage = !isPageViewTrigger(tour) || shouldShowOnPage(tour, pathname)
  return { enabled: tour.enabled && onPage, version: tour.version }
}

/**
 * The page-view tours that match `pathname`, as one string. `AppTourProvider` keys its settings
 * lookup on it, so the lookup changes identity (and each `Tour` runs its auto-start check again)
 * only when the set of matching tours changes, not on every navigation.
 */
export function matchingPageViewTourKey(
  tours: readonly ({ id: string } & PageTriggerFields)[] | null | undefined,
  pathname: string | null | undefined
): string {
  return (tours ?? [])
    .filter((tour) => shouldShowOnPage(tour, pathname))
    .map((tour) => tour.id)
    .join(',')
}

/** The `trigger` property of `tour:start` (#25610): how this opening came about. */
export function tourStartTrigger(autoStarted: boolean | undefined, tour?: PageTriggerFields | null): string {
  if (!autoStarted) return 'manual'
  return isPageViewTrigger(tour) ? 'page-view' : 'startup'
}
